import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FlightsSearchForm from "./search/FlightsSearchForm";
import FilterFlights from "./search/FilterFlights";
import { AlertDescription, AlertTitle } from "@/components/ui/alert";
import { XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  HiAdjustmentsHorizontal,
  HiMiniBarsArrowDown,
  HiMiniXMark,
} from "react-icons/hi2";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "../skeletons/LoadingSpinner";
import FlightListingSkeleton from "../skeletons/flightListSkeleton";
import Loading from "../common/Loading";
import { useMediaQuery } from "@/hooks/use-media-query";
import EmptyState from "./EmptyState";
import FlightListItem from "./FlightListItem";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import moment from "moment";
import { GetInfoById } from "../actions/offerBannerAction";

export default function FlightListing() {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { setSEO } = useSEO();

  const {
    filterTimes,
    filteredFlightList,
    flightFilters,
    flightList,
    flightSearchForm,
    searchForm,
    isFlightSearching,
  } = useSelector((state) => state.flights);

  const [showFilter, setShowFilter] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const offerId = searchParams.get("offerId");

  React.useEffect(() => {
    // when searching through offer, it's title will be use for SEO
    if (offerId) {
      if (window.initData?.offerInfo?.Title) {
        setSEO(
          window.initData.offerInfo.Title,
          window.initData.offerInfo.Description,
          window.initData.offerInfo.ImageUrl
        );
        return;
      } else {
        // will fetch offer info if not present in initial data
        dispatch(GetInfoById(offerId));
        return;
      }
    }

    const data = flightSearchForm?.originDestination?.[0] || searchForm;
    const departureDate = data?.departDateTime || searchForm?.departure;

    if (data?.origin && data?.destination) {
      //const dateStr = moment(departureDate).format("M/D, YYYY");
      const dateStr = moment(departureDate).format("MMM DD");
      //setSEO(`Flight Results: ${data.origin} to ${data.destination}, ${dateStr} | Yatra Tickets`);
      setSEO(`Flights for ${data.origin} to ${data.destination}, ${dateStr}`);
    } else if (searchForm?.from && searchForm?.to) {
      //const dateStr = moment(searchForm.departure).format("M/D, YYYY");
      const dateStr = moment(searchForm.departure).format("MMM DD");
      //setSEO(`Flight Results: ${searchForm.from} to ${searchForm.to}, ${dateStr} | Yatra Tickets`);
      setSEO(`Flights for ${searchForm.from} to ${searchForm.to}, ${dateStr}`);
    } else if (!offerId) {
      setSEO(`Flights | Yatra Tickets`);
    }
  }, [flightSearchForm, searchForm, setSEO, offerId, dispatch]);

  return (
    <>
      {isHolding ? (
        <Loading label="Processing... Please wait!" />
      ) : (
        <>
          {/* Search bar */}
          <FlightsSearchForm />

          {/* Main content */}
          <div className="container mx-auto px-3 pb-6">
            {isFlightSearching ? (
              <div className="flex justify-center pt-4">
                {flightSearchForm && (
                  <FlightListingSkeleton
                    tripTypeID={flightSearchForm.tripType}
                  />
                )}
              </div>
            ) : flightFilters && flightList && flightList.length > 0 ? (
              <div className="grid lg:grid-cols-4 grid-cols-1 lg:gap-4 lg:pt-4">
                {/* FILTER PANEL */}
                <div
                  className={cn(
                    // base: hidden on mobile, visible on lg
                    "hidden lg:block col-span-1",
                    // mobile overlay version when showFilter is true
                    showFilter &&
                    "fixed inset-0 z-50 block lg:relative lg:z-0",
                    isDesktop && "lg:pt-2"
                  )}
                >
                  {/* Backdrop for mobile filter */}
                  {showFilter && !isDesktop && (
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setShowFilter(false)}
                    />
                  )}

                  <div
                    className={cn(
                      "relative",
                      // full height on mobile drawer
                      "h-dvh lg:h-[calc(100dvh-160px)]",
                      "flex flex-col justify-between",
                      "bg-background border border-border",
                      "lg:top-20 lg:sticky lg:overflow-y-auto",
                      "lg:p-4"
                    )}
                  >
                    {/* Mobile filter header */}
                    <div className="flex lg:hidden items-center justify-between border-b border-border px-3 py-2 bg-background">
                      <h3 className="font-medium text-base flex-1">
                        Filter Flights
                      </h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowFilter(false)}
                      >
                        <HiMiniXMark className="h-5 w-5" />
                      </Button>
                    </div>

                    <FilterFlights
                      filteredFlightList={filteredFlightList}
                      flightList={flightList}
                      filterTimes={filterTimes}
                      totalFlights={flightList.length}
                      flightFilters={flightFilters}
                      setShowFilterCallback={(val) => {
                        setShowFilter(val);
                      }}
                    />
                  </div>
                </div>

                {/* LIST PANEL */}
                {filteredFlightList && filteredFlightList.length > 0 ? (
                  <div className="col-span-3 flex flex-col lg:pt-2">
                    {/* Mobile sort/filter controls */}
                    <div className="flex lg:hidden gap-x-2 py-3">
                      <Button variant="outline" className="h-8 px-3">
                        <HiMiniBarsArrowDown className="mr-2 h-4 w-4" />
                        <span className="text-xs">Sort</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-8 px-3"
                        onClick={() => {
                          setShowFilter(true);
                        }}
                      >
                        <HiAdjustmentsHorizontal className="mr-2 h-4 w-4" />
                        <span className="text-xs">Filter</span>
                      </Button>
                    </div>

                    {/* Flight list */}
                    <div className="space-y-3">
                      {filteredFlightList.map((flight) => (
                        <FlightListItem
                          key={"flight_" + flight.InventoryID + "_id"}
                          flight={flight}
                          IsHolding={(holding) => setIsHolding(holding)}
                        />
                      ))}
                    </div>
                  </div>
                ) : isFlightSearching ? (
                  <div className="col-span-3 flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="col-span-3 flex items-start gap-3 p-4">
                    <XCircle size={36} strokeWidth={1} className="mt-1" />
                    <div>
                      <AlertTitle>Sorry!</AlertTitle>
                      <AlertDescription>
                        The filtered data is not available. Clear your filter!
                      </AlertDescription>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </>
      )}
    </>
  );
}
